import localization from './localization';
import { load_preview, prepare_preview } from './preview';
import { init_page_state, clear_page_state } from '../utils/lib/state';
import {
  pageSnapshot,
  questionAttributes,
  tagLoaderError,
} from '../features/questions/common/telemetry';
import { answerVariablesOf } from '../modules/_shared/answers-adapter';
import {
  BLOCK_UNEXPECTED_KEYS,
  unexpectedPayloadKeys,
} from '../modules/Navigation/payload-check';

/** Report the payload keys the page never declared: the names, never the values. */
function report_payload(keys) {
  try {
    report.logError(
      new Error('Unexpected payload keys: ' + keys.join(', ')),
      true
    );
  } catch (e) {
    // Reporting must never block the submit or strand the respondent.
  }
}

/**
 * With `BLOCK_UNEXPECTED_KEYS`, a payload with undeclared keys is not sent
 * (RPI-1136): report the keys, show the generic error, and give the
 * respondent the buttons back, undoing what `handleNavigationEvent` disabled.
 * The join button is disabled only by Next (`__submit__: 1`).
 */
function reject_payload(data, keys) {
  report_payload(keys);
  $('#notification')
    .attr('class', 'alert alert-error')
    .html(
      '<h4>' + localization.localize('Oops! Something went wrong!') + '</h4>'
    )
    .show();
  mainNav.enable();
  if (data.__submit__ === 1) {
    mainNav.joinButton.enable();
  }
  loaded_buttons();
  $('.response-button').css({ 'pointer-events': '' });
}

/**
 * Report, for each React question (`react` or `react-recovered`) on the page
 * being left, its own variables the form answered for
 * (`dom_fallback_variables` / `dom_override_variables`): one silent report
 * per question, none when it has none (RPI-1138).
 */
function report_dom_answer_variables() {
  try {
    var fallback = page_state.dom_fallback_variables || [];
    var override = page_state.dom_override_variables || [];
    $.each(Gryphon.widgets.instances.toArray(), function (index, widget) {
      // One widget's failure must not drop the others' reports.
      try {
        var path = widget._renderPath;
        if (
          (path !== 'react' && path !== 'react-recovered') ||
          widget._destroyed
        ) {
          return;
        }
        var names = answerVariablesOf(widget._object);
        var own = function (list) {
          return list
            .filter(function (name) {
              return names.indexOf(name) !== -1;
            })
            .join(',');
        };
        var fallbackVars = own(fallback);
        var overrideVars = own(override);
        if (!fallbackVars && !overrideVars) {
          return;
        }
        var error = new Error(
          'React feature "' + widget._object.view + '" answered from the form'
        );
        error.cause = 'domAnswerVariables';
        report.logError(
          error,
          true,
          $.extend(questionAttributes(widget._object, path), {
            domFallbackVars: fallbackVars,
            domOverrideVars: overrideVars,
          })
        );
      } catch (e) {}
    });
  } catch (e) {
    // Reporting must never block the next page.
  }
}

global.before_load_new_question = function (res) {
  // The page being left, for the `Next` page action (RPI-1138)
  var outgoing;
  try {
    outgoing = pageSnapshot();
  } catch (e) {
    // Reporting must never block the next page.
    outgoing = {};
  }
  report_dom_answer_variables();
  Gryphon.util.inject_runtime_parameters(res);
  Gryphon.events.emit('before_new_question');
  Gryphon.widgets.instances.invoke('destroy');
  Gryphon.widgets.instances.empty();
  Gryphon.widgets.Widget.forgetPreloadFailures();
  if (res?.feature_flags) {
    global.configCatFeatureFlags.set(res?.feature_flags);
  }
  clear_page_state();
  mainNav.unbind();
  mainNav.hide();
  init_page_state(res);
  // Store __pg_context__ from response for next request
  if (res.__pg_context__) {
    page_state.__pg_context__ = res.__pg_context__;
  }
  debug_timing_push(2);
  if (check_redirect(res)) {
    return;
  }
  sendSurveystudioMetadata(res);
  shieldMonitoring();
  check_scroll(res);
  injectPartnerUrl(res);
  update_pbar(res);
  discover_variables(res);
  check_timing(res);

  var promises = [
    Gryphon.util.do_imports(),
    Gryphon.util.add_css(),
    Gryphon.util.add_js(),
  ];

  $.when
    .apply($, promises)
    .then(function () {
      $(window).trigger('load');
      load_new_question(res);
    })
    .fail(function (error) {
      load_new_question(res);
      // Silently log exception
      console.error('Failed to load', error);
      report.logError(error, true);
    });

  if (window.newrelic) {
    window.newrelic.addPageAction('Next', outgoing);
  }
};

global.load_new_question = function (res) {
  connect_page_timers(res);
  connect_nav_handlers(res);
  set_spd_elements_state(res);
  set_language_direction(res.rtl);
  localization.setLocale(res.language);
  set_page_name_visibility(res);
  display_feedback_widget(res);

  set_footer_text(res);

  var container = null;

  setTimeout(async () => {
    try {
      await localization.fetchLocalizations(res.language);

      if (res.language) {
        await global.set_input_error_text();
      }
    } catch (error) {
      console.error('Error fetching localization:', error);
      report.logError(error, true);
    }
  }, 1000); // Timeout added to allow rest of UI to render before triggering network request

  /*
      Occasionally, custom QSL Javascript is wrapped with
      $(page_state).on('before_content_loaded', function() {...}

      Will allow logging of any exceptions within the
      `before_content_loaded` event listener separately
    */
  try {
    Gryphon.events.emit('before_content_loaded');
  } catch (err) {
    // Rethrow the original error (its stack) with `cause` specified
    throw tagLoaderError(err, 'customQSLError');
  }

  // Stop the loading animation.
  clearInterval(window.loadingTimer);

  // Remove references to form elements about to be removed from the DOM.
  $.uniform.restore();

  if (res.html !== undefined) {
    container = res.container ? res.container : 'main_cont';
    $('#' + container).html(res.html);
    prepare_preview();
  }

  // Connects widget and template data from API (requires: container element from above).
  connect_widgets_or_template(res);

  if (nextQuestionStartTime) {
    var endTime = new Date().getTime();
    var timeSpent = endTime - nextQuestionStartTime;
    Gryphon.analytics.trackEvent('system', 'load questions', '', timeSpent);
  }

  // The `native` suffixed trigger is meant to be used only by IVW
  // natively - so we can discern between IVW and custom QSL
  Gryphon.events.emit('after_content_loaded_native');

  /*
      Generally, custom QSL Javascript is wrapped with
      $(page_state).on('after_content_loaded', function() {...}

      Will allow logging of any exceptions within the
      `after_content_loaded` event listener separately
    */

  setTimeout(function () {
    // After loading a new question, ensure we're at the top of the page
    $(window).scrollTop(0);

    load_preview();
  });
};

global.get_next_question = function (data) {
  // Handles navigation to root URL (without a VISA)
  if (!segments) {
    document.title = '404 Page Not Found';
    var template = Gryphon.templates['404'];
    return $('#main_cont').html(template);
  }

  data = data || {};
  var client = $.extend({}, Gryphon.util.get_client_caps());

  data.__client__ = JSON.stringify(client);
  data.__version__ = window.appVersion;

  nextQuestionStartTime = new Date().getTime();

  if (!process.env.API_BASE_URL) return;

  // When end of Preview is reached, send user to JSON API response
  if (segments.includes('session_summary')) {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    const ivwParam =
      params.ivwBE && params.ivwBE !== ''
        ? params.ivwBE.replace(/\/+$/, '')
        : null;
    const base =
      window.__ivwBE && window.__ivwBE !== ''
        ? window.__ivwBE
        : ivwParam || localStorage.getItem('ivw_be') || window.location.origin;
    window.location = base.replace(/\/+$/, '') + '/' + segments;
    return;
  }

  const qURL = (function () {
    if (window.context_name !== 'preview')
      return process.env.API_BASE_URL + 'q/' + segments;
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    const ivwParam =
      params.ivwBE && params.ivwBE !== ''
        ? params.ivwBE.replace(/\/+$/, '')
        : null;
    const base =
      window.__ivwBE && window.__ivwBE !== ''
        ? window.__ivwBE
        : ivwParam || localStorage.getItem('ivw_be') || window.location.origin;
    return (
      base.replace(/\/+$/, '') +
      '/' +
      segments.replace(/^preview\//, 'preview/q/')
    );
  })();

  // Set `qParams` to querystring when not submitting answers
  const qParams = set_query_params(data);

  var config = {
    url: qURL + qParams,
    type: 'post',
    dataType: 'json',
    data: data,
    traditional: true,
    timeout: window.ajax_timeout,
    xhrFields: {
      retries: window.ajax_error_max_tries,
      withCredentials: true,
    },
    success: before_load_new_question,
  };

  Gryphon.util.IE_do_not_cache(config);
  Gryphon.util.Firefox_include_content_length(config);

  var state = window.page_state || {};
  var unexpected = unexpectedPayloadKeys(data, state.allvars, state.vardata);
  if (unexpected.length) {
    if (BLOCK_UNEXPECTED_KEYS) {
      reject_payload(data, unexpected);
      return;
    }
    report_payload(unexpected);
  }

  $.ajax(config).fail(function (xhr) {
    // Handle Status Code 0, Status Text 'error'
    if (
      xhr.statusText === 'error' &&
      xhr.status === 0 &&
      config.xhrFields.retries > 0
    ) {
      try {
        // Get the current question
        $.ajax({
          url: qURL,
          type: 'get',
          data: false,
          traditional: true,
          timeout: window.ajax_timeout,
          success: function (res) {
            // Proceed with request retry only when on the same page
            if (
              res &&
              res.init_state &&
              res.init_state.this_page === page_state.this_page
            ) {
              console.log('retrying request...');
              config.xhrFields.retries--;
              $.ajax(config);
            }
          },
          error: function (res) {
            console.log('error : ' + res);
          },
        });
      } catch (e) {}
    } else if (xhr.status === 404 && window.context_name === 'preview') {
      if (!parent) return;

      // Expired preview
      parent.postMessage(
        {
          type: 'STATUS_RESPONSE',
          status: 'expired',
        },
        '*'
      );
    }
  });
};
